<template>
  <v-card border>
    <div
      ref="map"
      class="map"
    />
    <MapLegend
      v-if="map"
      :map="map"
      :markers="markers"
      @apply-filter="applyFilterToMap"
    />
  </v-card>
</template>

<script>
/* global google */
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import * as Sentry from '@sentry/vue'

import status from '@/lib/leadStatus'
import { status as ValuationStatus } from '@/lib/valuation'
import currencyMixin from '@/mixins/currency'
import mapMixin from '@/mixins/map'

import MapLegend from './MapLegend'

export default {
  components: { MapLegend },
  mixins: [currencyMixin, mapMixin],
  props: {
    leads: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      savedBounds: null,
      savedMarkerPosition: null,
      boundsListenerAttached: false,
    }
  },
  async mounted() {
    this.loadGoogleMaps()
    await this.googleMapsLoaded()

    const { lat, lng } = this.getDefaultCenter(this.$auth.user.locale)
    const zoom = this.getDefaultZoom(this.$auth.user.locale)

    this.map = new google.maps.Map(this.$refs.map, {
      center: { lat, lng },
      zoom,
      fullscreenControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    })

    const infoWindow = new google.maps.InfoWindow()

    google.maps.event.addListener(infoWindow, 'domready', () => {
      const links = document.getElementsByClassName('leadDetailLink')
      for (const link of links) {
        link.addEventListener('click', (event) =>
          this.$router.push('/leads/' + event.target.getAttribute('data'))
        )
      }
    })

    // Wait for the map to be fully loaded before processing markers
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.populateMap(infoWindow)
      this.createMarkerClusterer(infoWindow)

      // Add resize listener to reapply bounds whenever the map container changes size
      google.maps.event.addListener(this.map, 'resize', () => {
        this.reapplyMapState()
      })
    })
  },
  methods: {
    // Handle single marker with optional circle
    handleSingleMarker(marker) {
      this.map.setCenter(marker.position)

      const circle = this.circles.find((c) => c.getCenter().equals(marker.position))

      if (circle) {
        // If there's a circle, fit the map to show the entire circle
        try {
          const circleBounds = circle.getBounds()
          if (circleBounds) {
            try {
              this.map.fitBounds(circleBounds)
            } catch (error) {
              Sentry.captureException(error, {
                level: 'error',
                tags: {
                  component: 'MapComponent',
                  action: 'handleSingleMarker',
                  method: 'fitBounds',
                },
              })
              this.map.setZoom(13)
            }
          }
        } catch (error) {
          Sentry.captureException(error, {
            level: 'error',
            tags: {
              component: 'MapComponent',
              action: 'handleSingleMarker',
              method: 'getBounds',
            },
          })
          this.map.setZoom(13)
        }

        // Ensure minimum zoom level for readability
        const currentZoom = this.map.getZoom()
        if (currentZoom > 15) {
          this.map.setZoom(15)
        }
      } else {
        // No circle, use default zoom for single marker
        this.map.setZoom(13)
      }
    },

    // Reapply saved map state
    reapplyMapState() {
      if (this.savedBounds && this.markers.length > 1) {
        try {
          this.map.fitBounds(this.savedBounds)
        } catch (error) {
          Sentry.captureException(error, {
            level: 'error',
            tags: {
              component: 'MapComponent',
              action: 'reapplyMapState',
              method: 'fitBounds',
            },
          })
          // Fallback to default center and zoom
          const { lat, lng } = this.getDefaultCenter(this.$auth.user.locale)
          const zoom = this.getDefaultZoom(this.$auth.user.locale)
          this.map.setCenter({ lat, lng })
          this.map.setZoom(zoom)
        }
      } else if (this.savedMarkerPosition && this.markers.length === 1) {
        this.handleSingleMarker(this.markers[0])
      }
    },

    populateMap(infoWindow) {
      this.markers.forEach((marker) => {
        marker.setMap(null)
      })
      this.circles.forEach((circle) => {
        circle.setMap(null)
      })
      this.markers = []
      this.circles = []
      const bounds = new google.maps.LatLngBounds()

      if (!this.leads || this.leads.length === 0) {
        return
      }

      this.leads.forEach((lead) =>
        lead.conversions.forEach(({ valuation, propertyRequest }) => {
          if ((valuation && valuation.status === ValuationStatus.COMPLETED) || propertyRequest) {
            let address
            let type
            let price
            let radius

            if (valuation && valuation.status === ValuationStatus.COMPLETED) {
              const avg = valuation.results?.property?.value
              const currency = valuation.results?.currency
              address = {
                ...valuation.inputs.address,
                ...valuation.inputs.coordinates,
              }
              type = lead.currentStatus === 'new' ? 'active' : 'inactive'
              price = avg ? this.formatPrice(avg, currency) : 0
            } else if (propertyRequest) {
              address = {
                ...propertyRequest.property.location.coordinates,
                radius: propertyRequest.property.location.radius,
                description: propertyRequest.property.location.description,
              }
              type = 'propertyRequest'
              price = `${this.formatPrice(propertyRequest.property.minPrice)} - ${this.formatPrice(propertyRequest.property.maxPrice)}`
              radius = propertyRequest.property.location.radius
            }

            // Skip invalid coordinates
            if (!address || !address.lat || !address.lng) {
              return
            }

            const marker = this.createMarker({ type, address, radius, lead })
            let content

            if (type === 'propertyRequest') {
              content = this.createPropertyRequestMarkerInfo({ lead, address, price })
            } else if (['inactive', 'active'].includes(type)) {
              content = this.createValuationMarkerInfo({ lead, address, price })
            }

            marker.addListener('click', function () {
              infoWindow.setContent(content)
              infoWindow.open(this.map, marker)
            })

            this.markers.push(marker)

            // Always extend with the marker position
            bounds.extend(marker.position)

            if (radius) {
              const circle = this.createRadius(marker)
              this.circles.push(circle)

              // Only union with circle bounds if they're not global
              try {
                const circleBounds = circle.getBounds()
                if (
                  circleBounds &&
                  circleBounds.getNorthEast().lng() !== 180 &&
                  circleBounds.getSouthWest().lng() !== -180
                ) {
                  bounds.union(circleBounds, 0)
                }
              } catch (error) {
                Sentry.captureException(error, {
                  level: 'error',
                  tags: {
                    component: 'MapComponent',
                    action: 'populateMap',
                    method: 'getBounds',
                    context: 'circleBoundsUnion',
                  },
                })
              }
            }
          }
        })
      )

      if (this.markers.length > 0) {
        // For a single marker case
        if (this.markers.length === 1) {
          this.savedMarkerPosition = this.markers[0].position
          this.handleSingleMarker(this.markers[0])
        } else {
          // For multiple markers
          this.savedBounds = bounds

          // Check if bounds are valid and not empty
          if (!bounds.isEmpty()) {
            // Apply the bounds
            try {
              this.map.fitBounds(bounds)
            } catch (error) {
              Sentry.captureException(error, {
                level: 'error',
                tags: {
                  component: 'MapComponent',
                  action: 'populateMap',
                  method: 'fitBounds',
                },
              })
              // Fallback to default center and zoom
              const { lat, lng } = this.getDefaultCenter(this.$auth.user.locale)
              const zoom = this.getDefaultZoom(this.$auth.user.locale)
              this.map.setCenter({ lat, lng })
              this.map.setZoom(zoom)
            }

            // After fitting bounds, check if we need to zoom out more to show all circles
            if (!this.boundsListenerAttached) {
              this.boundsListenerAttached = true
              google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
                this.boundsListenerAttached = false
                const currentZoom = this.map.getZoom()

                // If zoom is too high and we have circles, zoom out a bit more
                if (currentZoom > 12 && this.circles.length > 0) {
                  // Check if any circles extend beyond current viewport
                  const mapBounds = this.map.getBounds()
                  const needsZoomOut = this.circles.some((circle) => {
                    try {
                      const circleBounds = circle.getBounds()
                      if (!circleBounds) return false
                      return (
                        !mapBounds.contains(circleBounds.getNorthEast()) ||
                        !mapBounds.contains(circleBounds.getSouthWest())
                      )
                    } catch (error) {
                      Sentry.captureException(error, {
                        level: 'error',
                        tags: {
                          component: 'MapComponent',
                          action: 'boundsChanged',
                          method: 'getBounds',
                          context: 'zoomCheck',
                        },
                      })
                      return false
                    }
                  })

                  if (needsZoomOut) {
                    this.map.setZoom(Math.max(currentZoom - 1, 8)) // Zoom out but not less than 8
                  }
                }
              })
            }
          }
        }
      }
    },

    createMarkerClusterer(infoWindow) {
      const renderer = {
        render: ({ markers, _position: position }) => {
          return new google.maps.Marker({
            position: {
              lat: position.lat(),
              lng: position.lng(),
            },
            icon: {
              url: '/img/marker/multiple.svg',
              scaledSize: { height: 50, width: 50 },
            },
            label: { text: String(markers.length), color: 'white', fontSize: '15px' },
          })
        },
      }

      this.markerClusterer = new MarkerClusterer({ markers: this.markers, map: this.map, renderer })
      this.markerClusterer.onClusterClick = (_, clickedCluster) => {
        return this.clusterClick(clickedCluster, this.markerClusterer, infoWindow)
      }
    },

    clusterClick(clickedCluster, markerClusterer, clusterInfoWindow) {
      var zoom = this.map.getZoom()
      var maxZoom = 15
      if (zoom >= maxZoom && markerClusterer.clusters.length > 1) {
        clusterInfoWindow.setContent(this.createMultipleMarkerInfo(clickedCluster.markers))
        clusterInfoWindow.open(this.map, clickedCluster.marker)
        return true
      } else {
        this.map.setCenter(clickedCluster.marker.position)
        this.map.setZoom(zoom + 2)
        return false
      }
    },

    createPropertyRequestMarkerInfo({ lead, address, price }) {
      const statusText = lead.currentStatus ? this.$t(status[lead.currentStatus] || '') : ''
      return `
        <div style="width:200px">
          <h2>${price}</h2>
          <br>
          <div>
            ${lead.firstname || ''} ${lead.lastname || ''}
          </div>
          <div>
            ${address.description}
          </div>
          <br>
          <div>
            ${statusText}
          </div>
          <br>
          <b><a class="leadDetailLink" data="${lead.id}">${this.$t('labels.lead-map.contact-info')}</a></b>
        </div>
      `
    },

    createValuationMarkerInfo({ lead, address, price }) {
      const statusText = lead.currentStatus ? this.$t(status[lead.currentStatus] || '') : ''
      return `
        <div style="width:200px">
          <h2>${price}</h2>
          <br>
          <div>
            ${lead.firstname || ''} ${lead.lastname || ''}
          </div>
          <div>
            ${address.street} ${address.houseNumber}
          </div>
          <div>
            ${address.zip} ${address.city}
          </div>
          <br>
          <div>
            ${statusText}
          </div>
          <br>
          <b><a class="leadDetailLink" data="${lead.id}">${this.$t('labels.lead-map.contact-info')}</a></b>
        </div>
      `
    },

    createMultipleMarkerInfo(markers) {
      let markersList = ''
      markers.forEach((marker) => {
        markersList += `
              <div style="max-width: 100%; overflow:hidden" class="d-flex py-2">
                    <img width="20px" height="20px" src="${marker.icon.url}" alt="type icon"/>
                    <span style="max-width: 100%" class="pt-1"><b><a class="leadDetailLink" data="${marker.lead.id}">${marker.lead.name || marker.lead.email}</a></b></span>
              </div>
            `
      })
      return `<div style="width:200px; max-height: 300px">
        <p><b>${markers.length}</b> ${this.$t('labels.lead-map.estates')}</p>
        <div>
          ${markersList}
        </div>
      </div>
      `
    },
  },
}
</script>

<style scoped>
.map {
  height: 400px;
  width: 100%;
}
</style>
